export interface TreeNode {
  el: HTMLInputElement
  level: number
  children: TreeNode[]
  parent: TreeNode | null
  visible?: boolean
}

export interface JsonTreeNode {
  id: string
  name: string
  checked: boolean
  fullyChecked: boolean
  fullyUnchecked: boolean
  children: JsonTreeNode[]
}

export class CheckboxTree {
  private nodes: TreeNode[] = []
  private readonly rootNode: TreeNode | null = null

  constructor (checkboxes: HTMLInputElement[]) {
    this.buildTree(checkboxes)
    this.rootNode = this.nodes.find(n => n.level === 0) ?? null
  }

  getRootNode (): TreeNode | null {
    return this.rootNode
  }

  findNodeByElement (element: HTMLInputElement): TreeNode | null {
    return this.findInNodeTree(this.nodes, element)
  }

  updateNodeState (node: TreeNode, checked: boolean): void {
    this.propagateDown(node, checked)
    if (node.children.length !== 0) {
      this.updateCheckedState(node)
    }
  }

  propagateChanges (node: TreeNode): void {
    let current = node.parent
    while (current != null) {
      this.updateCheckedState(current)
      current = current.parent
    }
  }

  updateTreeFromLeaves (): void {
    for (const node of this.nodes) {
      this.updateTreeFromLeavesRecursive(node)
    }
  }

  buildJsonTrees (): { checkedTree: JsonTreeNode[], uncheckedTree: JsonTreeNode[] } {
    if (this.rootNode == null) return { checkedTree: [], uncheckedTree: [] }

    const checkedTree: JsonTreeNode[] = []
    const uncheckedTree: JsonTreeNode[] = []

    this.rootNode.children.forEach(child => {
      const { checkedJson, uncheckedJson } = this.convertNodeToJson(child)
      if (checkedJson != null) checkedTree.push(checkedJson)
      if (uncheckedJson != null) uncheckedTree.push(uncheckedJson)
    })

    return { checkedTree, uncheckedTree }
  }

  filterTree (searchValue: string): void {
    const searchLower = searchValue.toLowerCase().trim()

    this.nodes.forEach(node => this.processNode(node, searchLower))
  }

  set allUnchecked (state: boolean) {
    this.nodes.forEach(node => this.updateNodeState(node, !state))
  }

  get allUnchecked (): boolean {
    return this.nodes.every(node => !node.el.checked)
  }

  get visibleNodesCount (): number {
    return this.countVisible(this.nodes)
  }

  private processNode (node: TreeNode, searchValue: string): boolean {
    if (node.level === 0) {
      node.children.forEach(child => this.processNode(child, searchValue))
      return true
    }

    const nodeMatches = this.matchesSearch(node, searchValue)
    const hasMatchingChildren = node.children.some(child => this.processNode(child, searchValue))

    const state = searchValue.length === 0 || nodeMatches || hasMatchingChildren
    this.toggleNode(node, state)

    return state
  }

  private matchesSearch (node: TreeNode, searchValue: string): boolean {
    if (searchValue.length === 0) return true

    const nodeText = this.getNodeName(node).toLowerCase()
    return nodeText.includes(searchValue) ||
      node.children.some(child => this.matchesSearch(child, searchValue))
  }

  toggleNode (node: TreeNode, state: boolean): void {
    if (node.level === 0) return

    node.visible = state
    const block = node.el.closest('[id^="option"]') as HTMLElement
    if (block != null) {
      block.style.display = state ? '' : 'none'
    }
  }

  private buildTree (checkboxes: HTMLInputElement[]): void {
    this.nodes = []
    const stack: TreeNode[] = []

    for (const checkbox of checkboxes) {
      const level = parseInt(checkbox.dataset.level ?? '0', 10)
      const node: TreeNode = {
        el: checkbox,
        level,
        children: [],
        parent: null,
        visible: true
      }

      while (stack.length > 0 && stack[stack.length - 1].level >= node.level) {
        stack.pop()
      }

      if (stack.length === 0) {
        this.nodes.push(node)
      } else {
        const parent = stack[stack.length - 1]
        node.parent = parent
        parent.children.push(node)
      }

      stack.push(node)
    }
  }

  private updateTreeFromLeavesRecursive (node: TreeNode): void {
    node.children.forEach(child => this.updateTreeFromLeavesRecursive(child))

    if (node.children.length !== 0) {
      this.updateCheckedState(node)
    }
  }

  private propagateDown (node: TreeNode, checked: boolean): void {
    node.el.checked = checked
    this.setIndeterminate(node, false)
    node.children.forEach(child => this.propagateDown(child, checked))
  }

  private updateCheckedState (node: TreeNode): void {
    const allChildrenSame = this.areAllChildrenSame(node)

    if (allChildrenSame) {
      this.setIndeterminate(node, false)
      node.el.checked = this.getUniformChildState(node)
    } else {
      this.setIndeterminate(node, true)
      node.el.checked = this.rootNode === node
    }
  }

  private findInNodeTree (nodes: TreeNode[], element: HTMLInputElement): TreeNode | null {
    for (const node of nodes) {
      if (node.el === element) return node

      const found = this.findInNodeTree(node.children, element)
      if (found != null) return found
    }
    return null
  }

  private convertNodeToJson (node: TreeNode): { checkedJson?: JsonTreeNode, uncheckedJson?: JsonTreeNode } {
    const childrenJson = node.children.map(this.convertNodeToJson.bind(this))
    const checkedChildren = childrenJson.map(c => c.checkedJson).filter(Boolean) as JsonTreeNode[]
    const uncheckedChildren = childrenJson.map(c => c.uncheckedJson).filter(Boolean) as JsonTreeNode[]

    const base: JsonTreeNode = {
      id: node.el.value,
      name: this.getNodeName(node),
      checked: node.el.checked || node.el.indeterminate,
      fullyChecked: this.isSubtreeFullyChecked(node),
      fullyUnchecked: this.isSubtreeFullyUnchecked(node),
      children: []
    }

    if (node.el.indeterminate) {
      return {
        checkedJson: { ...base, children: checkedChildren },
        uncheckedJson: { ...base, checked: false, fullyChecked: false, children: uncheckedChildren }
      }
    }

    return node.el.checked
      ? { checkedJson: { ...base, children: checkedChildren } }
      : { uncheckedJson: { ...base, checked: false, fullyChecked: false, children: uncheckedChildren } }
  }

  private countVisible (nodes: TreeNode[]): number {
    return nodes.reduce((sum, node) => {
      const selfVisible = node.level > 0 && node.visible === true ? 1 : 0

      return sum + selfVisible + this.countVisible(node.children)
    }, 0)
  }

  private getNodeName (node: TreeNode): string {
    const label = node.el.nextElementSibling as HTMLElement
    const labelText = label?.textContent?.trim()

    return (labelText != null && labelText.length > 0) ? labelText : (node.el.value.length > 0 ? node.el.value : '')
  }

  private isSubtreeFullyChecked (node: TreeNode): boolean {
    if (!node.el.checked || node.el.indeterminate) return false

    return node.children.every(child => this.isSubtreeFullyChecked(child))
  }

  private isSubtreeFullyUnchecked (node: TreeNode): boolean {
    if (node.el.checked || node.el.indeterminate) return false

    return node.children.every(child => this.isSubtreeFullyUnchecked(child))
  }

  private areAllChildrenSame (node: TreeNode): boolean {
    if (node.children.length === 0) return true
    if (node.children.some(child => child.el.indeterminate)) return false

    const firstChildState = node.children[0].el.checked

    return node.children.every(child => child.el.checked === firstChildState)
  }

  private getUniformChildState (node: TreeNode): boolean {
    return node.children[0]?.el.checked ?? false
  }

  private setIndeterminate (node: TreeNode, state: boolean): void {
    node.el.indeterminate = state
    node.el.classList.toggle('indeterminate', state)
  }
}
